import { useState } from "react";
import {
  INITIAL_LEAD_FORM,
  INITIAL_BULK_ACTION_FORM,
} from "../lib/constants";
import { getCityOptionsForRegion, toInputDateTime } from "../lib/utils";
import { CITY_REGION_MAP } from "../components/leads/LeadFilters";

export const INITIAL_DUPLICATE_STATE = {
  open: false,
  matches: [],
  payload: null,
  variant: "preview",
  leadId: null,
  message: "",
};

export const INITIAL_BULK_FEEDBACK = {
  error: "",
  notice: "",
};

export const INITIAL_DOCUMENT_FEEDBACK = {
  error: "",
  notice: "",
};

export function useLeadModals() {
  const [leadModal, setLeadModal] = useState({ open: false, mode: "create" });
  const [leadForm, setLeadForm] = useState(INITIAL_LEAD_FORM);
  const [journeyPickerOpen, setJourneyPickerOpen] = useState(false);
  const [journeyModalOpen, setJourneyModalOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [bulkModal, setBulkModal] = useState({ open: false, mode: "upload" });
  const [bulkForm, setBulkForm] = useState(INITIAL_BULK_ACTION_FORM);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkFeedback, setBulkFeedback] = useState(INITIAL_BULK_FEEDBACK);
  const [duplicateState, setDuplicateState] = useState(INITIAL_DUPLICATE_STATE);
  const [documentFeedback, setDocumentFeedback] = useState(INITIAL_DOCUMENT_FEEDBACK);

  function handleLeadFormChange(field, value, { geographyData } = {}) {
    setLeadForm((current) => {
      const next = { ...current, [field]: value };

      if (field === "regionName" && geographyData) {
        const nextCityOptions = getCityOptionsForRegion(
          geographyData.cityOptionsByRegion,
          value,
          geographyData.allCities,
        );
        if (current.cityName && !nextCityOptions.includes(current.cityName)) {
          next.cityName = "";
        }
      }

      if (field === "cityName" && value) {
        const mappedRegion = CITY_REGION_MAP[value];
        if (mappedRegion) {
          next.regionName = mappedRegion;
        }
      }

      if (field === "dispositionId") {
        next.subDispositionId = "";
        next.nextFollowUp = "";
      }

      return next;
    });
  }

  function handleBulkFormChange(field, value) {
    setBulkFeedback(INITIAL_BULK_FEEDBACK);
    setBulkForm((current) => ({ ...current, [field]: value }));
  }

  function openBulkUpload() {
    setBulkResult(null);
    setBulkFeedback(INITIAL_BULK_FEEDBACK);
    setBulkForm(INITIAL_BULK_ACTION_FORM);
    setBulkModal({ open: true, mode: "upload" });
  }

  function openBulkReassign() {
    setBulkResult(null);
    setBulkFeedback(INITIAL_BULK_FEEDBACK);
    setBulkForm(INITIAL_BULK_ACTION_FORM);
    setBulkModal({ open: true, mode: "reassign" });
  }

  function closeBulkModal() {
    setBulkModal({ open: false, mode: "upload" });
    setBulkForm(INITIAL_BULK_ACTION_FORM);
    setBulkFeedback(INITIAL_BULK_FEEDBACK);
    setBulkResult(null);
  }

  function populateLeadFormFromDetail(detail) {
    const savedWhatsapp = detail?.whatsapp_number || "";
    const isSameAsMobile = !savedWhatsapp || savedWhatsapp === (detail?.phone_number || "");
    setLeadForm({
      fullName: detail?.full_name || "",
      phoneNumber: detail?.phone_number || "",
      whatsappSameAsMobile: isSameAsMobile,
      whatsappNumber: isSameAsMobile ? "" : savedWhatsapp,
      email: detail?.email || "",
      subSource: detail?.sub_source || "",
      profession: detail?.profession || "",
      sourceId: String(detail?.source || ""),
      assignedToId: String(detail?.assigned_to || ""),
      regionName: detail?.region_name || "",
      cityName: detail?.city_name || "",
      pincode: detail?.pincode || "",
      notes: detail?.notes || "",
      dispositionId: String(detail?.current_disposition || ""),
      subDispositionId: String(detail?.current_sub_disposition || ""),
      nextFollowUp: toInputDateTime(detail?.next_follow_up_at),
    });
  }

  function resetModals() {
    setLeadModal({ open: false, mode: "create" });
    setLeadForm(INITIAL_LEAD_FORM);
    setJourneyPickerOpen(false);
    setJourneyModalOpen(false);
    setGuideOpen(false);
    setBulkModal({ open: false, mode: "upload" });
    setBulkForm(INITIAL_BULK_ACTION_FORM);
    setBulkResult(null);
    setBulkFeedback(INITIAL_BULK_FEEDBACK);
    setDuplicateState(INITIAL_DUPLICATE_STATE);
    setDocumentFeedback(INITIAL_DOCUMENT_FEEDBACK);
  }

  return {
    bulkFeedback,
    bulkForm,
    bulkModal,
    bulkResult,
    closeBulkModal,
    documentFeedback,
    duplicateState,
    guideOpen,
    handleBulkFormChange,
    handleLeadFormChange,
    journeyModalOpen,
    journeyPickerOpen,
    leadForm,
    leadModal,
    openBulkReassign,
    openBulkUpload,
    populateLeadFormFromDetail,
    resetModals,
    setBulkFeedback,
    setBulkForm,
    setBulkModal,
    setBulkResult,
    setDocumentFeedback,
    setDuplicateState,
    setGuideOpen,
    setJourneyModalOpen,
    setJourneyPickerOpen,
    setLeadForm,
    setLeadModal,
  };
}
